// src/pages/PoliciesPage.jsx
import React, { useEffect, useState, useContext } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Paper,
  TextField,
  Typography,
  Stack,
  Snackbar,
  Alert,
  Checkbox,
  TablePagination,
} from '@mui/material';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SecurityIcon from '@mui/icons-material/Security';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import BlockIcon from '@mui/icons-material/Block';
import PolicyIcon from '@mui/icons-material/Policy';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import DescriptionIcon from '@mui/icons-material/Description';
import { request } from '../utils/request';
import { ColorModeContext } from '../index';
import CommonDeleteModal from '../components/Common/CommonDeleteModal';

const getPolicyIconStyles = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('mfa')) return { icon: <SecurityIcon fontSize="small" />, color: '#1976D2', bgcolor: '#E3F2FD' };
  if (n.includes('dlp')) return { icon: <SecurityIcon fontSize="small" />, color: '#7B1FA2', bgcolor: '#F3E5F5' };
  if (n.includes('print')) return { icon: <PrintIcon fontSize="small" />, color: '#D32F2F', bgcolor: '#FFEBEE' };
  if (n.includes('download')) return { icon: <DownloadIcon fontSize="small" />, color: '#388E3C', bgcolor: '#E8F5E9' };
  if (n.includes('upload')) return { icon: <FileUploadIcon fontSize="small" />, color: '#E65100', bgcolor: '#FFF3E0' };
  if (n.includes('watermark')) return { icon: <BrandingWatermarkIcon fontSize="small" />, color: '#0288D1', bgcolor: '#E1F5FE' };
  if (n.includes('url') || n.includes('filter')) return { icon: <FilterAltIcon fontSize="small" />, color: '#00796B', bgcolor: '#E0F2F1' };
  if (n.includes('screen')) return { icon: <DesktopWindowsIcon fontSize="small" />, color: '#512DA8', bgcolor: '#EDE7F6' };
  if (n.includes('clipboard') || n.includes('copy')) return { icon: <ContentPasteIcon fontSize="small" />, color: '#C2185B', bgcolor: '#FCE4EC' };
  if (n.includes('block') || n.includes('tracker')) return { icon: <BlockIcon fontSize="small" />, color: '#7B1FA2', bgcolor: '#F3E5F5' };
  return { icon: <PolicyIcon fontSize="small" />, color: '#1976D2', bgcolor: '#E3F2FD' };
};

export default function PoliciesPage() {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === 'dark';

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = currentUser.superadmin === true;
  const userPermissions = currentUser.permissions || [];
  const hasPermission = (perm) => isSuperAdmin || userPermissions.includes(perm);

  const canCreate = hasPermission('Browser Control-Policies-PoliciesWrite');
  const canEdit = hasPermission('Browser Control-Policies-PoliciesUpdate'); // Separated from Write
  const canDelete = hasPermission('Browser Control-Policies-PoliciesDelete');

  const [policies, setPolicies] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
  });
  const [isEditing, setIsEditing] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingPolicy, setDeletingPolicy] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRowId, setMenuRowId] = useState(null);

  const handleMenuOpen = (event, id) => {
    setAnchorEl(event.currentTarget);
    setMenuRowId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuRowId(null);
  };

  const inputStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      fontFamily: 'Calibri',
      fontSize: '16px',
      color: 'var(--vault-navy)',
      minHeight: '48px',
      '& fieldset': {
        borderColor: 'var(--hairline)',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: 'var(--hairline)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--edge-blue)',
        borderWidth: '2px',
      },
      '&.Mui-focused': {
        boxShadow: '0 0 0 3px var(--blue-tint)',
      }
    },
    '& .MuiInputLabel-root': {
      fontFamily: 'Calibri',
      fontSize: '16px',
      color: 'var(--slate)',
      '&.Mui-focused': {
        color: 'var(--edge-blue)',
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '12.5px 14px',
    }
  };

  const onMenuEdit = (policy) => {
    if (!canEdit) return;
    handleMenuClose();
    handleOpen(policy);
  };

  const onMenuDelete = (policy) => {
    if (!canDelete) return;
    handleMenuClose();
    handleDeleteClick(policy);
  };

  useEffect(() => {
    fetchPolicies();
  }, []);

  useEffect(() => {
    setPage(0);
  }, [policies.length]);

  const paginatedPolicies = policies.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleChangePage = (event, nextPage) => setPage(nextPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const showSnack = (message, severity = 'success') => {
    setSnackMsg(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const handleSnackClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackOpen(false);
  };

  const fetchPolicies = async () => {
    try {
      const data = await request('policies', { method: 'get' });
      const arr = Array.isArray(data) ? data : [];
      const normalized = arr.map((r) => ({
        id: r._id,
        name: r.name,
        description: r.description,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt,
      }));
      setPolicies(normalized);
    } catch (err) {
      console.error('Error fetching Policies:', err);
      setPolicies([]);
      showSnack('Failed to fetch policies.', 'error');
    }
  };

  const handleOpen = (policy) => {
    if (policy) {
      setFormData(policy);
      setIsEditing(true);
    } else {
      setFormData({ id: null, name: '', description: '' });
      setIsEditing(false);
    }
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    if (!isEditing && !canCreate) return;
    if (isEditing && !canEdit) return;

    if (!formData.name.trim()) {
      showSnack('Policy Name is required.', 'error');
      return;
    }
    if (formData.description && formData.description.length > 500) {
      showSnack('Description cannot exceed 500 characters.', 'error');
      return;
    }

    try {
      const opts = isEditing
        ? { method: 'put', params: formData.id, body: formData }
        : { method: 'post', body: formData };
      await request('policies', opts);
      await fetchPolicies();
      handleClose();
      showSnack(isEditing ? 'Policy updated successfully.' : 'Policy created successfully.', 'success');
    } catch (err) {
      console.error('Error saving policy:', err);
      showSnack('Failed to save policy.', 'error');
    }
  };

  const handleDeleteClick = (policy) => {
    if (!canDelete) return;
    setDeletingPolicy(policy);
    setDeleteOpen(true);
  };

  const handleCancelDelete = () => {
    setDeletingPolicy(null);
    setDeleteOpen(false);
    setDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!deletingPolicy || !deletingPolicy.id) return;
    setDeleting(true);
    try {
      await request('policies', { method: 'delete', params: deletingPolicy.id });
      await fetchPolicies();
      showSnack(`Policy "${deletingPolicy.name}" successfully deleted.`, 'success');
      handleCancelDelete();
    } catch (err) {
      console.error('Failed to delete policy:', err);
      showSnack('Failed to delete policy.', 'error');
      setDeleting(false);
    }
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const isAllSelected = policies.length > 0 && selectedIds.length === policies.length;

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(policies.map((r) => r.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleEditSelected = () => {
    if (!canEdit) return;
    if (selectedIds.length !== 1) return;
    const p = policies.find((r) => r.id === selectedIds[0]);
    if (p) {
      handleOpen(p);
      setSelectedIds([]);
    }
  };

  const handleDeleteSelected = () => {
    if (!canDelete) return;
    if (selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  };

  const handleCancelBulkDelete = () => {
    setBulkDeleteOpen(false);
    setBulkDeleting(false);
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => request('policies', { method: 'delete', params: id })));
      setSelectedIds([]);
      await fetchPolicies();
      handleCancelBulkDelete();
      showSnack('Selected policies deleted.', 'success');
    } catch (err) {
      console.error('Bulk delete failed:', err);
      showSnack('Failed to delete selected policies.', 'error');
      setBulkDeleting(false);
    }
  };

  return (
    <Box p={3} sx={{ minHeight: '100vh', bgcolor: 'var(--mist)' }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link
          href="/home"
          underline="hover"
          sx={{
            fontFamily: 'Calibri',
            fontSize: '16px',
            color: 'var(--edge-blue)',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          Dashboard
        </Link>
        <Typography sx={{ fontFamily: 'Calibri', fontSize: '16px', fontWeight: 700, color: 'var(--vault-navy)' }}>
          Policies
        </Typography>
      </Breadcrumbs>

      <Stack 
        direction={{ xs: 'column', md: 'row' }} 
        justifyContent="space-between" 
        alignItems={{ xs: 'flex-start', md: 'center' }} 
        spacing={{ xs: 2, md: 0 }}
        mb={3}
      >
        <Box>
          <Typography sx={{ fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
            Manage browser control policies to enforce security and access rules across your organization.
          </Typography>
        </Box>
        {canCreate && (
          <Button
            variant="contained"
            onClick={() => handleOpen()}
            sx={{
              bgcolor: 'var(--edge-blue)',
              color: '#FFF',
              fontFamily: 'Arial',
              fontWeight: 'bold',
              fontSize: '16px',
              borderRadius: '6px',
              height: '40px',
              px: 3,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: 'var(--edge-blue)',
                boxShadow: '0 0 0 3px var(--blue-tint)',
              },
              '&:focus-visible': {
                outline: '2px solid var(--edge-blue)',
                outlineOffset: '2px',
              }
            }}
          >
            Add Policy
          </Button>
        )}
      </Stack>

      {selectedIds.length > 0 && (
        <Box mb={2} display="flex" justifyContent="flex-start" alignItems="center" gap={1}>
          <Typography sx={{ fontFamily: 'Calibri', fontSize: '16px', color: 'var(--vault-navy)', fontWeight: 700, mr: 1 }}>
            {selectedIds.length} selected
          </Typography>

          {canEdit && (
            <Button
              size="small"
              onClick={handleEditSelected}
              disabled={selectedIds.length !== 1}
              sx={{
                bgcolor: '#FFF',
                color: 'var(--vault-navy)',
                fontFamily: 'Arial',
                fontWeight: 'bold',
                fontSize: '14px',
                borderRadius: '6px',
                height: '32px',
                border: '1px solid var(--hairline)',
                textTransform: 'none',
                '&:hover:not(:disabled)': {
                  borderColor: 'var(--edge-blue)',
                  boxShadow: '0 0 0 3px var(--blue-tint)',
                  bgcolor: '#FFF',
                }
              }}
            >
              Edit
            </Button>
          )}

          {canDelete && (
            <Button
              size="small"
              onClick={handleDeleteSelected}
              sx={{
                bgcolor: '#FFF',
                color: 'var(--critical)',
                fontFamily: 'Arial',
                fontWeight: 'bold',
                fontSize: '14px',
                borderRadius: '6px',
                height: '32px',
                border: '1px solid var(--critical)',
                textTransform: 'none',
                '&:hover:not(:disabled)': {
                  bgcolor: 'rgba(214, 50, 58, 0.04)',
                }
              }}
            >
              Delete
            </Button>
          )}

          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{
              color: 'var(--edge-blue)',
              fontFamily: 'Arial',
              fontWeight: 'bold',
              fontSize: '14px',
              textTransform: 'none',
              '&:hover': {
                textDecoration: 'underline',
                bgcolor: 'transparent'
              }
            }}
          >
            Clear
          </Button>
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          bgcolor: 'var(--canvas)',
          border: '1px solid var(--hairline)',
          borderRadius: '12px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          mb: 3,
          boxShadow: 'none',
          height: 'calc(100vh - 180px)',
        }}
      >
        <Box
          sx={{
            height: 'calc(100% - 60px)',
            overflow: 'auto',
            width: '100%',
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--slate) transparent',
            '&::-webkit-scrollbar': { width: '6px', height: '6px' },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': { background: 'var(--slate)', borderRadius: '10px' },
          }}
        >
          <Box
            component="table"
            sx={{ 
              minWidth: 1000, 
              width: '100%', 
              borderCollapse: 'collapse',
              textAlign: 'left'
            }}
          >
            <Box component="thead" sx={{ bgcolor: '#FFF', borderBottom: '1px solid var(--hairline)' }}>
              <Box component="tr">
                <Box
                  component="th"
                  sx={{
                    p: '8px 16px',
                    width: 48,
                    textAlign: 'center',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: '#FFF',
                    borderBottom: '1px solid var(--hairline)',
                  }}
                >
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={selectedIds.length > 0 && !isAllSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    inputProps={{ 'aria-label': 'select all policies' }}
                    sx={{
                      p: '4px',
                      color: 'var(--slate)',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: 'var(--edge-blue)',
                      },
                      '& .MuiSvgIcon-root': { fontSize: '20px', borderRadius: '4px' }
                    }}
                  />
                </Box>

                {[
                  { label: '#', width: '5%' },
                  { label: 'Policy Name', width: '25%' },
                  { label: 'Description', width: '35%' },
                  { label: 'Created At', width: '15%' },
                  { label: 'Updated At', width: '15%' },
                  ...(canEdit || canDelete ? [{ label: 'Actions', width: '80px' }] : [])
                ].map((col, i) => (
                  <Box
                    component="th"
                    key={i}
                    textAlign={col.label === 'Actions' ? 'center' : 'left'}
                    sx={{
                      p: '8px 16px',
                      width: col.width,
                      color: 'var(--vault-navy)',
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '13px',
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      bgcolor: '#FFF',
                      borderBottom: '1px solid var(--hairline)',
                    }}
                  >
                    {col.label}
                  </Box>
                ))}
              </Box>
            </Box>

            <Box component="tbody">
              {paginatedPolicies.map((p, idx) => {
                const iconStyles = getPolicyIconStyles(p.name);
                return (
                <Box 
                  component="tr" 
                  key={p.id || idx}
                  sx={{
                    bgcolor: '#FFF',
                    borderBottom: '1px solid var(--hairline)',
                    '&:hover': { bgcolor: 'var(--mist)' },
                  }}
                >
                  <Box component="td" sx={{ p: '8px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <Checkbox
                      checked={selectedIds.includes(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      inputProps={{ 'aria-label': `select policy ${p.id}` }}
                      sx={{
                        p: '4px',
                        color: 'var(--slate)',
                        '&.Mui-checked': { color: 'var(--edge-blue)' },
                        '& .MuiSvgIcon-root': { fontSize: '20px', borderRadius: '4px' }
                      }}
                    />
                  </Box>

                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                    {page * rowsPerPage + idx + 1}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ 
                        width: 32, 
                        height: 32, 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        bgcolor: iconStyles.bgcolor,
                        color: iconStyles.color
                      }}>
                        {iconStyles.icon}
                      </Box>
                      <Typography sx={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', fontWeight: 600, color: 'var(--vault-navy)' }}>
                        {p.name}
                      </Typography>
                    </Box>
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                    {p.description || <Typography component="span" sx={{ color: 'var(--slate)' }}>—</Typography>}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                    {p.createdAt ? new Date(p.createdAt).toLocaleString() : '—'}
                  </Box>
                  <Box component="td" sx={{ p: '8px 16px', verticalAlign: 'middle', fontFamily: 'Calibri', fontSize: '14px', color: 'var(--slate)' }}>
                    {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'}
                  </Box>

                  {(canEdit || canDelete) && (
                    <Box component="td" sx={{ p: '8px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                      <IconButton
                        size="small"
                        aria-label={`more-actions-${p.id}`}
                        onClick={(e) => handleMenuOpen(e, p.id)}
                        sx={{
                          width: '32px',
                          height: '32px',
                          color: 'var(--slate)',
                          '&:hover': {
                            bgcolor: 'var(--mist)',
                            color: 'var(--vault-navy)',
                          }
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>

                      <Menu
                        id={`row-menu-${p.id}`}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuRowId === p.id}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        MenuListProps={{ 'aria-labelledby': `more-actions-${p.id}` }}
                        PaperProps={{ 
                          sx: { 
                            bgcolor: 'var(--canvas)',
                            border: '1px solid var(--hairline)',
                            boxShadow: '0 4px 12px rgba(11, 37, 69, 0.08), 0 1px 2px rgba(11, 37, 69, 0.04)',
                            borderRadius: '12px',
                            mt: 0.5,
                          }
                        }}
                      >
                        {canEdit && (
                          <MenuItem 
                            onClick={() => onMenuEdit(p)}
                            sx={{ fontFamily: 'Calibri', fontSize: '14px', color: 'var(--vault-navy)', '&:hover': { bgcolor: 'var(--mist)' } }}
                          >
                            Edit
                          </MenuItem>
                        )}
                        {canDelete && (
                          <MenuItem 
                            onClick={() => onMenuDelete(p)}
                            sx={{ fontFamily: 'Calibri', fontSize: '14px', color: 'var(--critical)', '&:hover': { bgcolor: 'var(--mist)' } }}
                          >
                            Delete
                          </MenuItem>
                        )}
                      </Menu>
                    </Box>
                  )}
                </Box>
              )})}
              
              {policies.length === 0 && (
                <Box component="tr">
                  <Box component="td" colSpan={canEdit || canDelete ? 6 : 5} sx={{ p: 3, textAlign: 'center', fontFamily: 'Calibri', fontSize: '16px', color: 'var(--slate)' }}>
                    No policies found.
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <TablePagination
          component="div"
          count={policies.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ 
            flexShrink: 0,
            borderTop: '1px solid var(--hairline)',
            color: 'var(--slate)',
            fontFamily: 'Calibri',
            fontSize: '14px',
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
              fontFamily: 'Calibri',
              fontSize: '14px',
              color: 'var(--slate)',
            },
            '& .MuiTablePagination-selectIcon': {
              color: 'var(--slate)',
            },
            '& .MuiIconButton-root': {
              color: 'var(--slate)',
              '&.Mui-disabled': {
                color: 'var(--hairline)',
              }
            }
          }}
        />
      </Paper>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        sx={{
          '& .MuiDialog-paper': {
            width: '1000px',
            maxWidth: '1000px',
            borderRadius: '16px',
            bgcolor: 'var(--canvas)',
            boxShadow: '0 8px 32px rgba(11, 37, 69, 0.15)',
            border: 'none',
            overflow: 'hidden',
            margin: { xs: '16px', md: '24px' },
            maxHeight: { xs: 'calc(100% - 32px)', md: 'calc(100% - 48px)' },
            height: { xs: 'calc(100% - 32px)', md: 'auto' }
          },
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(11, 37, 69, 0.5)',
            backdropFilter: 'blur(5px)'
          }
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, height: { xs: '100%', md: '750px' }, maxHeight: { xs: '100%', md: '85vh' }, flexGrow: 1 }}>
          {/* Left Panel */}
          <Box
            sx={{
              width: { xs: '100%', md: '35%' },
              display: { xs: 'none', md: 'flex' },
              background: isDark ? 'linear-gradient(180deg, #0b1120 0%, #151C2B 100%)' : 'linear-gradient(180deg, #F4F7FB 0%, #E2EDFC 100%)',
              p: { xs: 4, md: 6 },
              '@media (max-height: 850px)': { p: 4 },
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0
            }}
          >
            {/* CSS Illustration Background */}
            <Box sx={{
              position: 'absolute',
              bottom: '-20px',
              right: '-10px',
              width: '140px',
              height: '140px',
              borderRadius: '24px',
              background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
              boxShadow: '0 10px 40px rgba(59, 130, 246, 0.3)',
              zIndex: 0,
              transform: 'rotate(15deg)',
              '@media (max-height: 850px)': {
                display: 'none'
              }
            }} />
            <Box sx={{
              position: 'absolute',
              bottom: '10px',
              left: '20px',
              width: '100px',
              height: '100px',
              borderRadius: '16px',
              background: isDark ? 'rgba(15, 23, 42, 0.6)' : 'rgba(255, 255, 255, 0.6)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.05)',
              border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.8)',
              zIndex: 1,
              transform: 'rotate(-10deg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '@media (max-height: 850px)': {
                display: 'none'
              }
            }}>
              <SecurityIcon sx={{ fontSize: 48, color: '#3B82F6', opacity: 0.9 }} />
            </Box>

            {/* Content Container (elevated z-index) */}
            <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Box sx={{ width: 56, height: 56, bgcolor: '#E8F0FE', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, '@media (max-height: 850px)': { mb: 2, width: 48, height: 48 } }}>
                <SecurityIcon sx={{ color: '#1A73E8', fontSize: 32, '@media (max-height: 850px)': { fontSize: 24 } }} />
              </Box>
              <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 900, fontSize: '32px', color: isDark ? '#F8FAFC' : '#0F172A', mb: 1.5, letterSpacing: '-0.5px', '@media (max-height: 850px)': { fontSize: '24px', mb: 1 } }}>
                {isEditing ? 'Edit Policy' : 'Add Policy'}
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '14px', color: isDark ? '#94A3B8' : '#64748B', mb: 5, lineHeight: 1.6, '@media (max-height: 850px)': { mb: 3 } }}>
                {isEditing ? 'Modify this browser control policy to update security rules and access restrictions.' : 'Create a new browser control policy to enforce security rules and access restrictions across your organization.'}
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, '@media (max-height: 850px)': { gap: 2.5 } }}>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <DescriptionIcon sx={{ color: '#4F46E5', fontSize: 22 }} />
                  </Box>
                  <Box sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 800, fontSize: '15px', color: isDark ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>Define Rules</Typography>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: isDark ? '#94A3B8' : '#64748B' }}>Set clear rules and restrictions for browser usage.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <GroupsIcon sx={{ color: '#059669', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 800, fontSize: '15px', color: isDark ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>Improve Security</Typography>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: isDark ? '#94A3B8' : '#64748B' }}>Control access and prevent unwanted activities.</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: '#F3E8FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <LockIcon sx={{ color: '#9333EA', fontSize: 20 }} />
                  </Box>
                  <Box sx={{ pt: 0.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontWeight: 800, fontSize: '15px', color: isDark ? '#F8FAFC' : '#1E293B', mb: 0.5 }}>Easy Management</Typography>
                    <Typography sx={{ fontFamily: 'Inter, Arial, sans-serif', fontSize: '13px', color: isDark ? '#94A3B8' : '#64748B' }}>Apply policies to groups and manage them centrally.</Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Right Panel */}
          <Box sx={{ width: { xs: '100%', md: '65%' }, display: 'flex', flexDirection: 'column', position: 'relative', height: '100%' }}>
            <IconButton onClick={handleClose} sx={{ position: 'absolute', top: 16, right: 16, color: 'var(--slate)', zIndex: 10 }}>
              <CloseIcon />
            </IconButton>

            <Box 
              sx={{ 
                p: { xs: 3, md: 5 }, 
                pt: { xs: 6, md: 5 }, 
                pb: 2, 
                flexGrow: 1, 
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'var(--hairline)', borderRadius: '4px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'var(--slate)' }
              }}
            >
              <Stack spacing={4}>
                <Box>
                  <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1, mt: { xs: 1, md: 0 } }}>
                    Policy Name <span style={{ color: 'var(--critical)' }}>*</span>
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Enter policy name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    fullWidth
                    InputProps={{
                      startAdornment: <DescriptionIcon sx={{ color: 'var(--slate)', mr: 1, fontSize: 20 }} />
                    }}
                    sx={{
                      ...inputStyles,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyles['& .MuiOutlinedInput-root'],
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF',
                      }
                    }}
                  />
                  <Typography sx={{ fontFamily: 'Calibri', fontSize: '13px', color: 'var(--slate)', mt: 1 }}>
                    Use a clear and descriptive name for this policy.
                  </Typography>
                </Box>

                <Box>
                  <Typography sx={{ fontFamily: 'Arial', fontWeight: 'bold', fontSize: '14px', color: 'var(--vault-navy)', mb: 1 }}>
                    Description
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Enter policy description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={6}
                    inputProps={{ maxLength: 500 }}
                    InputProps={{
                      startAdornment: <DescriptionIcon sx={{ color: 'var(--slate)', mr: 1, mt: 1.5, alignSelf: 'flex-start', fontSize: 20 }} />
                    }}
                    sx={{
                      ...inputStyles,
                      '& .MuiOutlinedInput-root': {
                        ...inputStyles['& .MuiOutlinedInput-root'],
                        bgcolor: isDark ? 'rgba(15, 23, 42, 0.6)' : '#FFF',
                        alignItems: 'flex-start',
                        padding: '12px',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: 0,
                      }
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography sx={{ fontFamily: 'Calibri', fontSize: '13px', color: 'var(--slate)' }}>
                      Describe what this policy does and how it controls access.
                    </Typography>
                    <Typography sx={{ fontFamily: 'Calibri', fontSize: '12px', color: 'var(--slate)' }}>
                      {(formData.description || '').length}/500
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
            
            <Box sx={{ p: 3, px: { xs: 3, md: 5 }, display: 'flex', justifyContent: 'flex-end', gap: 2, borderTop: '1px solid var(--hairline)', bgcolor: 'var(--canvas)', flexShrink: 0 }}>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  color: 'var(--vault-navy)',
                  borderColor: 'var(--hairline)',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '6px',
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'var(--mist)',
                    borderColor: 'var(--slate)'
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                sx={{
                  bgcolor: 'var(--edge-blue)',
                  color: '#FFF',
                  fontFamily: 'Arial',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  borderRadius: '6px',
                  px: 4,
                  py: 1,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: 'var(--edge-blue)',
                    boxShadow: '0 0 0 3px var(--blue-tint)',
                  }
                }}
              >
                {isEditing ? 'Update Policy' : '+ Create Policy'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>

      {/* Delete Single Item Dialog */}
      <CommonDeleteModal
        open={deleteOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={deleting}
        entityName={deletingPolicy ? deletingPolicy.name : ''}
        entityType="Policy"
      />

      {/* Bulk Delete Dialog */}
      <CommonDeleteModal
        open={bulkDeleteOpen}
        onClose={handleCancelBulkDelete}
        onConfirm={handleConfirmBulkDelete}
        isDeleting={bulkDeleting}
        entityType="Policy"
        isBulk={true}
        bulkCount={selectedIds.length}
      />

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }} className="global-alert">
          {snackMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}